import React, { useState, useEffect } from "react";
import Layout from '../pages/Layout';
import Advertise from "../pages/Advertise";

const ConstructionMaterial = () => {
  // State variables for Material Calculation
  const [length, setLength] = useState(0); // Length of the material
  const [width, setWidth] = useState(0); // Width of the material
  const [thickness, setThickness] = useState(0); // Thickness of the material
  const [materialVolume, setMaterialVolume] = useState(0); // Calculated volume of material
  const [lengthUnit, setLengthUnit] = useState("meters"); // Unit for length
  const [widthUnit, setWidthUnit] = useState("meters"); // Unit for width
  const [thicknessUnit, setThicknessUnit] = useState("meters"); // Unit for thickness

  // Event handlers to update state values
  const handleLengthChange = (e) => setLength(parseFloat(e.target.value) || 0);
  const handleWidthChange = (e) => setWidth(parseFloat(e.target.value) || 0);
  const handleThicknessChange = (e) => setThickness(parseFloat(e.target.value) || 0);

  // Event handlers to change units
  const handleLengthUnitChange = (e) => setLengthUnit(e.target.value);
  const handleWidthUnitChange = (e) => setWidthUnit(e.target.value);
  const handleThicknessUnitChange = (e) => setThicknessUnit(e.target.value);

  // Convert units to meters (standard unit)
  const convertToMeters = (value, unit) => {
    if (unit === "centimeters") {
      return value / 100; // 1 cm = 0.01 m
    }
    if (unit === "millimeters") {
      return value / 1000; // 1 mm = 0.001 m
    }
    if (unit === "inches") {
      return value * 0.0254; // 1 inch = 0.0254 m
    }
    if (unit === "feet") {
      return value * 0.3048; // 1 foot = 0.3048 m
    }
    if (unit === "yards") {
      return value * 0.9144; // 1 yard = 0.9144 m
    }
    return value; // Already in meters
  };

  // useEffect hook to calculate material volume when any input changes
  useEffect(() => {
    // Convert all inputs to meters before calculating volume
    const lengthInMeters = convertToMeters(length, lengthUnit);
    const widthInMeters = convertToMeters(width, widthUnit);
    const thicknessInMeters = convertToMeters(thickness, thicknessUnit);

    // Calculate the volume (Length x Width x Thickness)
    const volume = lengthInMeters * widthInMeters * thicknessInMeters;
    setMaterialVolume(volume);
  }, [length, width, thickness, lengthUnit, widthUnit, thicknessUnit]);

  return (
    <Layout title="Construction Material Calculator" path="construction-material">
      <section className="section-padding" id="material-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Material Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Construction Material Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Length Input with Unit Selection */}
                    <div className="col-md-4">
                      <label htmlFor="length" className="form-label">
                        Length
                      </label>
                      <div className="d-flex">
                        <input
                          type="number"
                          className="form-control"
                          id="length"
                          placeholder="Enter length"
                          value={length || ""}
                          onChange={handleLengthChange}
                        />
                        <select
                          className="form-select ms-2"
                          value={lengthUnit}
                          onChange={handleLengthUnitChange}
                        >
                          <option value="meters">meters</option>
                          <option value="centimeters">centimeters</option>
                          <option value="millimeters">millimeters</option>
                          <option value="inches">inches</option>
                          <option value="feet">feet</option>
                          <option value="yards">yards</option>
                        </select>
                      </div>
                    </div>

                    {/* Width Input with Unit Selection */}
                    <div className="col-md-4">
                      <label htmlFor="width" className="form-label">
                        Width
                      </label>
                      <div className="d-flex">
                        <input
                          type="number"
                          className="form-control"
                          id="width"
                          placeholder="Enter width"
                          value={width || ""}
                          onChange={handleWidthChange}
                        />
                        <select
                          className="form-select ms-2"
                          value={widthUnit}
                          onChange={handleWidthUnitChange}
                        >
                          <option value="meters">meters</option>
                          <option value="centimeters">centimeters</option>
                          <option value="millimeters">millimeters</option>
                          <option value="inches">inches</option>
                          <option value="feet">feet</option>
                          <option value="yards">yards</option>
                        </select>
                      </div>
                    </div>

                    {/* Thickness Input with Unit Selection */}
                    <div className="col-md-4">
                      <label htmlFor="thickness" className="form-label">
                        Thickness
                      </label>
                      <div className="d-flex">
                        <input
                          type="number"
                          className="form-control"
                          id="thickness"
                          placeholder="Enter thickness"
                          value={thickness || ""}
                          onChange={handleThicknessChange}
                        />
                        <select
                          className="form-select ms-2"
                          value={thicknessUnit}
                          onChange={handleThicknessUnitChange}
                        >
                          <option value="meters">meters</option>
                          <option value="centimeters">centimeters</option>
                          <option value="millimeters">millimeters</option>
                          <option value="inches">inches</option>
                          <option value="feet">feet</option>
                          <option value="yards">yards</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Material Volume:{" "}
                        <span className="fw-bold ms-3">
                          {materialVolume === 0 ? "0.00" : materialVolume.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} cubic meters
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Construction Material Calculator
                </h3>

                {/* What is a Construction Material Calculator? */}
                <h4 className="mt-3 mb-3">What is a Construction Material Calculator?</h4>
                <p>
                  The **Construction Material Calculator** helps estimate the amount of material needed for construction projects based on the dimensions of the material being used. Whether you are working with concrete, wood, or other materials, this tool provides a quick and accurate way to estimate the required quantities.
                </p>

                {/* Why Use a Construction Material Calculator? */}
                <h4 className="mt-4 mb-3">Why Use a Construction Material Calculator?</h4>
                <p>
                  Accurately estimating the volume of materials is crucial for budgeting and efficient project planning. This tool helps prevent over- or under-ordering, saving time and resources.
                </p>

                {/* Benefits of Using a Construction Material Calculator */}
                <h4 className="mt-4 mb-3">Benefits of Using a Construction Material Calculator</h4>
                <ul>
                  <li><strong>Helps in budgeting and material procurement:</strong> It ensures accurate material estimates, preventing excess costs.</li>
                  <li><strong>Reduces waste and excess costs:</strong> By calculating the precise amount needed, unnecessary material waste is minimized.</li>
                  <li><strong>Supports efficient project planning:</strong> Helps streamline procurement and avoid delays during construction.</li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use a Construction Material Calculator?</h4>
                <p>
                  The Construction Material Calculator is ideal for:
                </p>
                <ul>
                  <li><strong>Construction Companies:</strong> For planning material quantities in large-scale projects.</li>
                  <li><strong>Contractors:</strong> To estimate material needs on a smaller scale and budget effectively.</li>
                  <li><strong>Civil Engineers:</strong> For precise material volume calculations based on design specifications.</li>
                </ul>

                {/* Two Additional Questions */}
                <h4 className="mt-4 mb-3">How Does the Material Volume Affect the Cost?</h4>
                <p>
                  The volume of material directly impacts the cost of construction. Accurate volume estimation helps prevent over-ordering or under-ordering, ensuring the project stays within budget.
                </p>

                <h4 className="mt-4 mb-3">Can the Calculator Be Used for Multiple Types of Materials?</h4>
                <p>
                  Yes, the calculator can be used for various materials. It is versatile and can be applied to any material that requires volume estimation, such as concrete, wood, steel, and more.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ConstructionMaterial;
